import React, {useState} from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import {isFieldTypeSupportedForManualEntry} from 'notebook/data-tables/DataTableConfigEditUtil';
import ResultsTableAdapter from 'notebook/integrations/results/ResultsTableAdapter';
import useSelectorsById, {
  SchemaFieldSelectorsDict,
  selectorsAreLoaded,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import QueryingResultSchemaSelector from 'results/ResultSchemaSelector';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Modal from 'ui-library/modals/Modal';

import {ResultsTableMetadata} from './ResultsTableConstants';
import {ResultsTablePreview} from './ResultsTablePreview';

/**
 * Options while they are being actively configured in the modal
 */
interface ResultsTableInsertOptions {
  // Configured by the user
  schema: ResultSchema | null;
  metadata: ResultsTableMetadata;

  // Loaded via query
  selectorsById: SchemaFieldSelectorsDict | AsyncStatus.LOADING | AsyncStatus.ERROR | AsyncStatus.NOT_FETCHED;
}

/**
 * Options that have been validated as submittable
 */
interface ValidResultsTableInsertOptions {
  // Configured by the user
  schema: ResultSchema;
  metadata: ResultsTableMetadata;

  // Loaded via query
  selectorsById: SchemaFieldSelectorsDict;
}

function areOptionsValid(options: ResultsTableInsertOptions): options is ValidResultsTableInsertOptions {
  const {schema, selectorsById} = options;
  return schema != null && selectorsAreLoaded(selectorsById);
}

export interface ResultsTableInsertModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (options: ValidResultsTableInsertOptions) => void;
}

export default function ResultsTableInsertModal({
  onRequestClose,
  onRequestSubmit,
}: ResultsTableInsertModalProps): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const {resultSchema, resultSchemaSelector} = useResultSchemaSelector();
  const selectorsById = useSelectorsById({includeArchivedSelectors: false});
  const options = {
    schema: resultSchema,
    metadata: {
      supportsManualEntry: true,
      numResults: 0,
    },
    selectorsById,
  };

  let tablePreview: JSX.Element | null = null;

  if (resultSchema != null && areOptionsValid(options)) {
    const schemaColumnGroup = {...resultSchema, columnGroup: null};
    tablePreview = (
      <ResultsTablePreview
        schemaAndColumnGroup={schemaColumnGroup}
        adapter={new ResultsTableAdapter(StructuredTableKind.RESULTS)}
        selectorsById={selectorsById as {[selectorId: string]: SchemaFieldSelector}}
      />
    );
  }

  return (
    <Modal
      title="Insert result table"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        if (submitted) {
          // prevent double clicks
          return;
        }
        setSubmitted(true);
        assert(
          areOptionsValid(options),
          'Unexpected submission with invalid options. Expecting submit button to be disabled'
        );
        onRequestSubmit(options);
      }}
      buttons={[
        {
          text: 'Insert',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: submitted || !areOptionsValid(options),
          dataTestAttrs: {component: 'ResultsTableInsertModal', key: ModalButtonKey.INSERT},
        },
      ]}
      className="mediocre" // TableEditable's CSS is namespaced under mediocre
    >
      {resultSchemaSelector}
      {tablePreview}
    </Modal>
  );
}

function useResultSchemaSelector(): {
  resultSchema: ResultSchema | null;
  resultSchemaSelector: JSX.Element;
} {
  const [resultSchema, setResultSchema] = useState<ResultSchema | null>(null);

  const resultSchemaSelector = (
    <FormGroup dataTestAttrs={{component: 'ResultsTableInsertModal', element: 'resultsSchema'}}>
      <Label>Result schema</Label>
      <QueryingResultSchemaSelector
        selectedSchema={resultSchema}
        onChange={setResultSchema}
        filter={(schema) => isSchemaSupportedForResultsStructuredTable(schema)}
        requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
        renderThrobberWhileLoading={true}
      />
    </FormGroup>
  );

  return {resultSchema, resultSchemaSelector};
}

/**
 * Decide if a schema can be used with a manual result table.
 *
 * For easier migration off of legacy result tables, schemas with fields not supported for manual
 * entry can still be inserted, as long as there is at least one supported field.
 */
function isSchemaSupportedForResultsStructuredTable(schema: ResultSchema): boolean {
  // Exclude archived schemas
  if (isArchived(schema)) {
    return false;
  }

  // Exclude schemas with no fields
  if (schema.fields.length === 0) {
    return false;
  }

  // At least one field must be supported for manual entry
  if (schema.fields.every((field) => !isFieldTypeSupportedForManualEntry(field.fieldType))) {
    return false;
  }

  return true;
}
